// src/services/alertsService.ts
import { IAlertsFilterState } from '../redux/slices/alertSlice'
import { AlertNote } from '../components/ViewEntity/TabsView/AlertsView/interfaces'
import { axiosInstance, axiosInstanceEntity } from './axiosInstance'
import { AxiosResponse } from 'axios'

export interface FetchAlertsParams {
    pageNo?: number
    pageSize?: number
    searchText?: string
    searchAttributeName?: string
    searchAttributeType?: string
    fromDate?: string
    toDate?: string
    orderAttribute?: string
    orderBy?: string
    filters?: any[]
    localTimeZone?: string
    entitySearch?: boolean
    alertStartDate?: string
    alertEndDate?: string
    alertSeverity?: string[]
    alertType?: string[]
    alertStatus?: number[]
    alertSortAttribute?: string
    alertSortOrder?: string
}

export const fetchAlerts = async (
    orgId: string,
    templateId: string,
    params: FetchAlertsParams,
): Promise<any> => {
    const encodedParams = {
        ...params,
        localTimeZone:
            params.localTimeZone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    return await axiosInstanceEntity
        .post(`/alerts/query`, {
            templateId,
            orgId,
            ...encodedParams,
        })
        .then((response: AxiosResponse) => {
            return response?.data
        })
        .catch((err: any) => {
            console.error('fetchAlerts error', err)
            return { alerts: [], totalRowCount: 0 }
        })
}

export interface UnreadAlertCountResponse {
    filteredCount: number
    totalUnreadCount: number
}

/**
 * Fetches the count of unread alerts (status = 0).
 * Returns both filtered count (matching applied filters) and total unread count.
 */
export const fetchUnreadAlertCount = async (
    orgId: string,
    templateId: string,
    params: FetchAlertsParams,
): Promise<UnreadAlertCountResponse> => {
    const encodedParams = {
        ...params,
        localTimeZone:
            params.localTimeZone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    return await axiosInstanceEntity
        .post(`/alerts/unread-count`, {
            templateId,
            orgId,
            ...encodedParams,
        })
        .then((response: AxiosResponse) => {
            return response?.data || { filteredCount: 0, totalUnreadCount: 0 }
        })
        .catch((err: any) => {
            console.error('fetchUnreadAlertCount error', err)
            return { filteredCount: 0, totalUnreadCount: 0 }
        })
}

export const updateUserAlertPreferences = async (
    userId: string,
    orgId: string,
    preferences: Partial<IAlertsFilterState>,
): Promise<unknown> => {
    try {
        const url = `/users/updateAlertPreferences?userId=${userId}&orgId=${orgId}`

        const payload = {
            ...preferences,
        }

        const response = await axiosInstance.patch(url, payload)
        return response.data
    } catch (error) {
        console.error('Failed to update user alert preferences', error)
        throw error
    }
}

export const updateAlertStatus = async (
    orgId: string,
    payload: {
        entityId: string
        ruleId: string
        id: string
        timestamp: number
        status: number
    },
): Promise<AxiosResponse> => {
    return axiosInstanceEntity.put(`alerts/status`, payload, {
        params: { orgId },
    })
}

export interface AddAlertNoteRequest {
    entityId: string
    ruleId: string
    alertId: string
    alertTimestamp: number
    noteText: string
}

export const addAlertNote = async (
    orgId: string,
    payload: AddAlertNoteRequest,
): Promise<AlertNote> => {
    return axiosInstanceEntity
        .post(`/alerts/note`, payload, {
            params: { orgId },
        })
        .then((response: AxiosResponse) => response.data)
        .catch((err: any) => {
            console.error('addAlertNote error', err)
            throw err
        })
}

export const exportAlertsToExcel = async (
    orgId: string,
    templateId: string,
    params: FetchAlertsParams,
    selectedColumns: string[],
): Promise<any> => {
    // 1. Prepare parameters exactly like fetchAlerts
    const encodedParams = {
        ...params,
        localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Add export specific fields
        selectedColumns,
        // Backend expects 'startDate'/'endDate' for alerts, but often maps fromDate/toDate.
        // We send both sets to ensure compatibility with the DTO.
        startDate: params.fromDate,
        endDate: params.toDate,
    }

    return await axiosInstanceEntity
        .post(
            `/alerts/export`,
            {
                templateId,
                orgId,
                ...encodedParams,
            },
            {
                responseType: 'blob', // Important: This tells Axios to handle the response as a file
            },
        )
        .then((response: AxiosResponse) => {
            return response // Return full response to access headers for filename
        })
        .catch((err: any) => {
            console.error('exportAlertsToExcel error', err)
            throw err
        })
}
